import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, ArrowLeft, Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export const TermsPage = () => {
  const { t, isRTL, language, setLanguage, supportedLanguages } = useLanguage();

  return (
    <div className={`min-h-screen bg-background`} dir={isRTL ? 'rtl' : 'ltr'}>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl text-foreground">{t("appName")}</span>
          </Link>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[130px]">
                <Globe className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {supportedLanguages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.nativeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to="/">
              <Button variant="ghost" size="sm" className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />{t("backToHome")}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className={`max-w-4xl mx-auto prose prose-gray dark:prose-invert ${isRTL ? 'text-right' : 'text-left'}`}>
          <h1 className="font-heading text-4xl font-bold mb-8">{t("termsOfService")}</h1>
          
          <p className="text-muted-foreground mb-8">{t("lastUpdated")}: {t("termsLastUpdateDate")}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("terms.section1Title")}</h2>
            <p>{t("terms.section1Content")}</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("terms.section2Title")}</h2>
            <p>{t("terms.section2Intro")}</p>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} mt-4 space-y-2`}>
              <li>{t("terms.service1")}</li>
              <li>{t("terms.service2")}</li>
              <li>{t("terms.service3")}</li>
              <li>{t("terms.service4")}</li>
              <li>{t("terms.service5")}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("terms.section3Title")}</h2>
            <p>{t("terms.section3Intro")}</p>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} mt-4 space-y-2`}>
              <li>{t("terms.account1")}</li>
              <li>{t("terms.account2")}</li>
              <li>{t("terms.account3")}</li>
              <li>{t("terms.account4")}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("terms.section4Title")}</h2>
            <p>{t("terms.section4Intro")}</p>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} mt-4 space-y-2`}>
              <li><strong>{t("terms.planStarter")}:</strong> {t("terms.planStarterDesc")}</li>
              <li><strong>{t("terms.planGrowth")}:</strong> {t("terms.planGrowthDesc")}</li>
              <li><strong>{t("terms.planBusiness")}:</strong> {t("terms.planBusinessDesc")}</li>
              <li><strong>{t("terms.planEnterprise")}:</strong> {t("terms.planEnterpriseDesc")}</li>
            </ul>
            <p className="mt-4">{t("terms.paymentNote")}</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("terms.section5Title")}</h2>
            <p>{t("terms.section5Content")}</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("terms.section6Title")}</h2>
            <p>{t("terms.section6Intro")}</p>
            <ul className={`${isRTL ? 'list-disc pr-6' : 'list-disc pl-6'} mt-4 space-y-2`}>
              <li>{t("terms.acceptable1")}</li>
              <li>{t("terms.acceptable2")}</li>
              <li>{t("terms.acceptable3")}</li>
              <li>{t("terms.acceptable4")}</li>
              <li>{t("terms.acceptable5")}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("terms.section7Title")}</h2>
            <p>{t("terms.section7Content")}</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("terms.section8Title")}</h2>
            <p>{t("terms.section8Content")}</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">{t("terms.section9Title")}</h2>
            <p>{t("terms.section9Content")}</p>
            <p className="mt-2">{t("email")}: geocheck.inc@gmail.com</p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
